import React, { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import usertour from "usertour.js";
import { useAppState } from "../../context/StateContext";
import { LocationDescriptor } from "history";
import * as Sentry from "@sentry/react";

export default function UsertourBootstrap() {
  const history = useHistory();
  const { currentUser } = useAppState();

  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      const errorMessage =
        error && typeof error === "object" && "message" in error
          ? String(error.message)
          : String(error);
      const errorStack =
        error && typeof error === "object" && "stack" in error ? String(error.stack) : "";

      const isUsertourError =
        errorMessage.includes("Could not load Usertour.js") ||
        errorMessage.includes("usertour") ||
        errorStack.includes("usertour.es.js") ||
        errorStack.includes("usertour.js");

      if (isUsertourError) {
        event.preventDefault();
        console.error("[UsertourBootstrap] Erro capturado (unhandled rejection):", error);
        Sentry.captureException(error, {
          tags: { component: "UsertourBootstrap", action: "unhandled-rejection" },
        });
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    const token = process.env.REACT_APP_USERTOUR_TOKEN;
    if (!token) {
      return;
    }

    try {
      if (typeof usertour === "undefined" || !usertour.init) {
        console.warn("[UsertourBootstrap] usertour.js não está disponível. Verifique se o script foi carregado corretamente.");
        return;
      }

      usertour.init(token);
      usertour.setCustomNavigate((url: LocationDescriptor<unknown>) => history.push(url));
    } catch (error) {
      console.error("[UsertourBootstrap] Erro ao inicializar usertour:", error);
      Sentry.captureException(error, {
        tags: { component: "UsertourBootstrap", action: "init" },
      });
    }

    return () => {
      try {
        if (usertour && usertour.setCustomNavigate) {
          usertour.setCustomNavigate(null);
        }
      } catch (error) {
        console.error("[UsertourBootstrap] Erro ao limpar usertour:", error);
        Sentry.captureException(error, {
          tags: { component: "UsertourBootstrap", action: "cleanup" },
        });
      }
    };
  }, [history]);

  const mapped = useMemo(() => {
    if (!currentUser) return null;

    const fullName =
      currentUser.fullName ||
      [currentUser.name, currentUser.lastName].filter(Boolean).join(" ").trim();

    return {
      userId: String(currentUser.id),
      traits: {
        name: fullName || undefined,
        email: currentUser.email || currentUser.username,
        username: currentUser.username,
        is_admin: !!currentUser.isAdmin,
        status: currentUser.status,
        verified: !!currentUser.verified,
        email_confirmed: !!currentUser.emailConfirmed,
        receive_notifications: !!currentUser.receiveNotifications,
        leader: !!currentUser.leader,
        app_only: !!currentUser.appOnly,
        role: currentUser.typeUser,
        timezone: currentUser.timezone?.name,
        timezone_offset: currentUser.timezone?.gmtOffset,
        resources: (currentUser.resources || []).map((r: any) => r.code),
        apps: (currentUser.applications || []).map((a: any) => a.name),
        branches: (currentUser.branches || []).map((b: any) => b.code),
      },
      company: currentUser.company && {
        id: String(currentUser.company.id),
        traits: {
          name: currentUser.company.name,
          plan: currentUser.company.profile?.name,
          is_military: !!currentUser.company.isMilitary,
          country: currentUser.company.location?.country,
          city: currentUser.company.location?.suburb,
        },
        membership: {
          role: currentUser.typeUser,
          is_admin: !!currentUser.isAdmin,
        },
      },
    };
  }, [currentUser]);

  useEffect(() => {
    if (!mapped) return;

    if (typeof usertour === "undefined" || !usertour.identify) {
      console.warn("[UsertourBootstrap] usertour.js não está disponível para identificar usuário.");
      return;
    }

    usertour
      .identify(mapped.userId, mapped.traits)
      .then(() => {
        const params = new URLSearchParams(window.location.search);
        const flowId =
          params.get("usertour") || params.get("ut_flow") || process.env.REACT_APP_USERTOUR_FLOW;

        if (flowId) {
          usertour
            .start(flowId, { once: true })
            .catch((error: Error) => {
              console.warn("[UsertourBootstrap] Erro ao iniciar flow do usertour:", error);
            });
          params.delete("usertour");
          params.delete("ut_flow");
          const qs = params.toString();
          const base = window.location.pathname + (qs ? `?${qs}` : "");
          history.replace(base + window.location.hash);
        }
      })
      .catch((error: Error) => {
        console.error("[UsertourBootstrap] Erro ao identificar usuário no usertour:", error);
        Sentry.captureException(error, {
          tags: { component: "UsertourBootstrap", action: "identify" },
        });
      });
  }, [mapped, history]);

  return null;
}
